import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	githubIcon,
	googleDriveIcon,
	microsoftSharepointIcon,
	slackIcon,
} from '@atlassian/third-party-connector-configuration/images';

const CONNECTOR_ICON_MAP: Record<string, string> = {
	'github-connector': githubIcon,
	'platform-google-drive-connector': googleDriveIcon,
	'platform-sharepoint-connector': microsoftSharepointIcon,
	'slack-connector': slackIcon,
};

interface ConnectorIconPlaceholderProps {
	name: string;
	connectorKey?: string;
	iconUrl?: string;
	size?: 16 | 20 | 24;
}

const styles = cssMap({
	placeholder16: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.neutral'),
	},
	placeholder20: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '20px',
		height: '20px',
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.neutral'),
	},
	placeholder24: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.neutral'),
	},
});

const placeholderStyleMap = {
	16: styles.placeholder16,
	20: styles.placeholder20,
	24: styles.placeholder24,
} as const;

/**
 * Renders a connector icon from iconUrl, or a letter-based placeholder if unavailable.
 * Replace with actual product logo components when available.
 */
export const ConnectorIconPlaceholder = ({
	name,
	connectorKey,
	iconUrl,
	size = 24,
}: ConnectorIconPlaceholderProps) => {
	const resolvedIcon = iconUrl ?? (connectorKey ? CONNECTOR_ICON_MAP[connectorKey] : undefined);
	if (resolvedIcon) {
		return (
			<Box xcss={placeholderStyleMap[size]} role="img" aria-label={name}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
				<img src={resolvedIcon} alt="" style={{ maxHeight: '100%', width: 'auto' }} />
			</Box>
		);
	}

	return (
		<Box xcss={placeholderStyleMap[size]} role="img" aria-label={name}>
			<Text size="small" weight="semibold" color="color.text.subtle">
				{name.charAt(0).toUpperCase()}
			</Text>
		</Box>
	);
};
